import React, { useCallback, useMemo, useState } from 'react';
import { styled } from '@compiled/react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useMutation } from 'react-relay';
import ChevronDown from '@atlaskit/icon/core/chevron-down';
import Button from '@atlaskit/button';
import DropdownMenu, {
	type OnOpenChangeArgs,
	DropdownItem,
	DropdownItemGroup,
} from '@atlaskit/dropdown-menu';

import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { useFlagService, toFlagId } from '@atlassian/jira-flags';
import type { FlagCustomRenderProps } from '@atlassian/jira-flags/src/services/types';
import { useIntl } from '@atlassian/jira-intl';
import { ErrorFlag } from '@atlassian/jira-issue-navigator-actions-common/src/ui/error-flag/index.tsx';
import { SuccessFlag } from '@atlassian/jira-issue-navigator-actions-common/src/ui/success-flag/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import {
	useAnalyticsEvents,
	fireUIAnalytics,
	fireTrackAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { saveFilterDialogEntryPoint } from '@atlassian/jira-save-filter-dialog/src/ui/save-filter-dialog/entrypoint.tsx';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import ThirdPartyNudge from '@atlassian/jira-third-party-nudge/src/ui/async.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { ModalEntryPointDropdownItemTrigger } from '@atlassian/jira-modal-entry-point-dropdown-item-trigger/src/ModalEntryPointDropdownItemTrigger.tsx';
import type { mainUpdateFilterJqlMutation } from './__generated__/mainUpdateFilterJqlMutation.graphql';
import messages from './messages.tsx';

export type Props = {
	defaultOpen?: boolean;
	filterId?: string;
	query: string;
	onFilterSave?: (filterId: string) => void;
};

export const SUCCESS_FLAG_ID: string = toFlagId('issue-navigator-save-filter-success-flag');
export const ERROR_FLAG_ID: string = toFlagId('issue-navigator-save-filter-error-flag');

export const SaveFilterDropdown = ({
	defaultOpen = false,
	filterId,
	query,
	onFilterSave,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();

	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [updateJiraCustomFilterJqlMutation] = useMutation<mainUpdateFilterJqlMutation>(graphql`
		mutation mainUpdateFilterJqlMutation($id: ID!, $jql: String!) {
			jira {
				jiraFilterMutation {
					updateJiraCustomFilterJql(input: { id: $id, jql: $jql }) {
						success
						errors {
							message
						}
						filter {
							id
							name
							jql
						}
					}
				}
			}
		}
	`);

	const { activationId, cloudId } = useTenantContext();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({});
	const onSaveChanges = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			'saveFilterChanges',
		);
		filterId !== undefined &&
			updateJiraCustomFilterJqlMutation({
				variables: {
					id: createAri({
						resourceOwner: 'jira',
						cloudId,
						resourceType: 'filter',
						resourceId: filterId,
						activationId,
					}),
					jql: query,
				},
				onCompleted: ({ jira }) => {
					if (jira?.jiraFilterMutation?.updateJiraCustomFilterJql) {
						const {
							success,
							filter,
							errors: jiraErrors,
						} = jira?.jiraFilterMutation?.updateJiraCustomFilterJql;

						if (success && filter) {
							const { name } = filter;
							fireTrackAnalytics(
								createAnalyticsEvent({
									actionSubject: 'issueFilterJql',
									action: 'updated',
								}),
								'issueFilterJql updated',
							);
							showFlag({
								id: SUCCESS_FLAG_ID,
								render: (renderProps: FlagCustomRenderProps) => (
									<SuccessFlag
										id={SUCCESS_FLAG_ID}
										messageId="issue-navigator-custom-filters.ui.save-filter-dropdown.success-flag"
										messageType="transactional"
										filterName={name}
										// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
										viewUrl={`${window.location.origin}/issues/?filter=${filterId}`}
										analyticsEvent={createAnalyticsEvent({
											actionSubject: 'button',
											action: 'clicked',
										})}
										{...renderProps}
									/>
								),
							});
						} else {
							const errors = jiraErrors ? jiraErrors.map((error) => error.message || '') : [];
							showFlag({
								id: ERROR_FLAG_ID,
								render: (renderProps: FlagCustomRenderProps) => (
									<ErrorFlag
										id={ERROR_FLAG_ID}
										messageId="issue-navigator-custom-filters.ui.save-filter-dropdown.error-flag"
										messageType="transactional"
										errors={errors}
										{...renderProps}
									/>
								),
							});
							fireErrorAnalytics({
								event: analyticsEvent,
								error: new Error(errors[0]),
								meta: {
									id: 'updateFilterJqlMutation',
									packageName: 'jiraIssueNavigatorCustomFilters',
									teamName: 'empanada',
								},
							});
						}
					}
				},
				onError: (error) => {
					showFlag({
						id: ERROR_FLAG_ID,
						render: (renderProps: FlagCustomRenderProps) => (
							<ErrorFlag
								id={ERROR_FLAG_ID}
								messageId="issue-navigator-custom-filters.ui.save-filter-dropdown.error-flag.1"
								messageType="transactional"
								{...renderProps}
								errors={['']}
							/>
						),
					});
					fireErrorAnalytics({
						event: analyticsEvent,
						error,
						meta: {
							id: 'updateFilterJqlMutation',
							packageName: 'jiraIssueNavigatorCustomFilters',
							teamName: 'empanada',
						},
					});
					return undefined;
				},
			});
	}, [
		activationId,
		cloudId,
		createAnalyticsEvent,
		filterId,
		query,
		showFlag,
		updateJiraCustomFilterJqlMutation,
		analyticsEvent,
	]);

	const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen);
	const [shouldShowThirdPartyNudge, setShouldShowThirdPartyNudge] = useState(false);

	const onOpenSaveFilterModal = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			'saveFilterCopy',
		);
	}, [createAnalyticsEvent]);

	const modalProps = {
		width: 'medium',
		shouldScrollInViewport: true,
		shouldReturnFocus: false,
	};

	const entryPointProps = useMemo(
		() => ({
			jql: query,
			filterId,
			onFilterSave,
			title: formatMessage(messages.copyFilterDialogTitle),
		}),
		[query, filterId, onFilterSave, formatMessage],
	);

	const entryPointParams = useMemo(
		() => ({
			filterAri:
				filterId !== null && filterId !== undefined
					? createAri({
							resourceOwner: 'jira',
							cloudId,
							resourceType: 'filter',
							resourceId: filterId,
							activationId,
						})
					: undefined,
		}),
		[filterId, cloudId, activationId],
	);

	const onTriggerClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'toggled',
				actionSubject: 'dropdownMenu',
			}),
			'filterOptions',
		);
	}, [createAnalyticsEvent]);

	return (
		<>
			{shouldShowThirdPartyNudge && <ThirdPartyNudge />}
			<DropdownMenuWrapper>
				<DropdownMenu
					isOpen={isDropdownOpen}
					onOpenChange={(attrs: OnOpenChangeArgs) => {
						setIsDropdownOpen(attrs.isOpen);
						if (attrs.isOpen) {
							setShouldShowThirdPartyNudge(true);
							onTriggerClick();
						}
					}}
					trigger={({ triggerRef, ...props }) => (
						<Button
							{...props}
							appearance="link"
							ref={triggerRef}
							iconAfter={<ChevronDown label={formatMessage(messages.saveFilter)} size="small" />}
						>
							{formatMessage(messages.saveFilter)}
						</Button>
					)}
					shouldRenderToParent={fg('should-render-to-parent-should-be-true-empanada')}
				>
					<DropdownItemGroup>
						<DropdownItem onClick={onSaveChanges}>
							{formatMessage(messages.saveChanges)}
						</DropdownItem>
						<ModalEntryPointDropdownItemTrigger
							onClick={onOpenSaveFilterModal}
							modalProps={modalProps}
							entryPoint={saveFilterDialogEntryPoint}
							entryPointProps={entryPointProps}
							entryPointParams={entryPointParams}
							interactionName="jiraSaveFilterDialog.SaveFilter"
							useInternalModal
						>
							{formatMessage(messages.saveAsACopy)}
						</ModalEntryPointDropdownItemTrigger>
					</DropdownItemGroup>
				</DropdownMenu>
			</DropdownMenuWrapper>
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownMenuWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	"[data-role='droplistContent']": {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
		width: '218px !important',
	},
});
